import React, { ReactElement, useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { Modal, Row, Col } from 'react-bootstrap'
import Button from 'src/components/buttons/Button'
import Paragraph from 'src/components/Paragraph/Paragraph'
import Switch from 'src/components/inputs/Switch/SwitchV2'
import Loading from 'src/components/Loading/Loading'
import FormText from 'src/components/inputs/FormTextField/FormText'

import { faFileInvoice, faWindowClose } from '@fortawesome/free-solid-svg-icons'

import {
  actionTypes as orderActions,
  verifyOrderUseRecipe,
} from 'src/actions/orders.actions'
import { selectOrderUseRecipe } from 'src/selectors/orders.selector'

import {
  actionTypes as resolutionsActions,
  nullifyInvoiceAndOrder,
  reTryNullify,
} from 'src/actions/resolutions.actions'

import { isAllowed } from 'src/selectors/modules.selector'
import {
  selectCompanyCountry,
  selectCompanyFieldById,
} from 'src/selectors/company.selector'

import { loadingSelector } from 'src/selectors/loading.selector'
import { handlerError, handlerSuccess, hasErrors } from 'src/selectors/error.selector'

import { orderPermissions } from 'src/enums/permissions'
import { companyFieldsSell } from 'src/enums/companyFields'
import { showAlert } from 'src/actions/alert.actions'

import { Country } from 'src/enums/countries'
import { valEmail } from 'src/utils/utilities'
import { Card } from 'src/components'

interface IProps {
  show: boolean
  orderId: number
  number: string
  email?: string
  isDTE?: boolean
  retry?: boolean
  onHide: (update: boolean) => void
}

/** Render a modal to cancel order
 * @param {IProps} props properties of component
 * @param {boolean} props.show Indicates whether the order cancellation modal should be displayed or not.
 * @param {number} props.orderId The ID of the order to be canceled.
 * @param {string} props.number The number of the order to be canceled.
 * @param {string} props.email The email from client
 * @param {bottom} props.isDTE Indicates if de sale order have DTE
 * @param {void} props.onHide Function called when the cancellation modal is closed.
 * **/

const OrderNullify = ({
  show,
  orderId,
  number,
  onHide,
  isDTE = false,
  email,
  retry = false,
}: IProps) => {
  const dispatch = useDispatch()

  const country = useSelector(selectCompanyCountry)
  const isSV = country.id === Country.SV

  const loadingCancelBill: boolean = useSelector(state =>
    loadingSelector([resolutionsActions.CANCEL_BILL])(state),
  )
  const hasErrorCancelBill = useSelector(state =>
    hasErrors([resolutionsActions.CANCEL_BILL])(state),
  )
  const loadingVerifyOrder: boolean = useSelector(state =>
    loadingSelector([orderActions.VERIFY_ORDER_USE_RECIPES])(state),
  )
  const useRecipe: boolean = useSelector(selectOrderUseRecipe)

  /*PERMISSIONS*/
  const canChangeInventoryReturnOptionByUser: boolean = useSelector(state =>
    isAllowed(state, [orderPermissions.canChangeInventoryReturnOption]),
  )

  /*CONFIG*/
  const canChangeInventoryReturnOptionByCompany: boolean =
    useSelector(state =>
      selectCompanyFieldById(
        state,
        companyFieldsSell.canChangeInventoryReturnOptionByCompany,
      ),
    )?.value === 'true'

  const inventoryReturnIsDisabled =
    !(canChangeInventoryReturnOptionByUser && canChangeInventoryReturnOptionByCompany) ||
    loadingCancelBill

  const validateEmail = isSV && isDTE

  const [flags, setFlags] = useState({ nullify: false })
  const [returnInventory, setReturnInventory] = useState<boolean>(true)
  const [returnInventoryInsume, setReturnInventoryInsume] = useState<boolean>(true)
  const [nullifyEmail, setEmail] = useState<string>(null)
  const [emailError, setEmailError] = useState<string>(null)

  useEffect(() => {
    if (!show) return
    dispatch(verifyOrderUseRecipe(orderId))
    setEmail(email)
  }, [show])

  useEffect(() => {
    if (loadingCancelBill) setFlags({ ...flags, nullify: true })
    else if (flags.nullify) {
      setFlags({ ...flags, nullify: false })

      let alert
      if (hasErrorCancelBill) alert = handlerError(hasErrorCancelBill.message)
      else {
        onClose(true)
        alert = handlerSuccess()
      }

      dispatch(showAlert(alert))
    }
  }, [loadingCancelBill])

  const onClose = (update: boolean) => {
    onHide(update)
    setReturnInventory(true)
    setReturnInventoryInsume(true)
    setEmail(null)
  }

  const onNullify = () => {
    if (retry) {
      dispatch(reTryNullify(orderId))
      return
    }

    if (validateEmail && !valEmail(nullifyEmail)) {
      setEmailError('El formato del correo electrónico no es correcto')
    } else {
      dispatch(
        nullifyInvoiceAndOrder(orderId, {
          returnProduct: returnInventory,
          returnInsume: returnInventoryInsume,
          email: nullifyEmail,
        }),
      )
    }
  }

  const renderNullify = (): ReactElement => {
    return (
      <Row>
        <Col xl={12}>
          <Paragraph>¿Deseas anular la orden seleccionada?</Paragraph>
        </Col>
        <Col xl={12}>
          <Paragraph>Las transacciones monetarias serán revertidas.</Paragraph>
        </Col>
        <Col xl={12}>
          <Switch
            disabled={inventoryReturnIsDisabled || loadingCancelBill}
            checked={returnInventory}
            onChange={value => {
              setReturnInventory(value)
              if (!value) setReturnInventoryInsume(false)
            }}
            label={'Devolución de inventario'}
            info={'El producto total de la orden será devuelto a la bodega de origen'}
            id={'inventory-return'}
          />
        </Col>

        <Col xl={12}>
          <Loading show={loadingVerifyOrder} />
        </Col>

        {useRecipe && (
          <Col xl={12}>
            <Switch
              disabled={inventoryReturnIsDisabled || !returnInventory}
              checked={returnInventoryInsume}
              onChange={setReturnInventoryInsume}
              label={'Devolución de inventario de insumos'}
              info={
                'Los insumos utilizados en la elaboración del producto final serán devueltos a la bodega de origen'
              }
              id={'inventory-return-insume'}
            />
          </Col>
        )}

        {validateEmail && (
          <Col xl={12}>
            <FormText
              label={'Correo de notificación'}
              value={nullifyEmail}
              changeValue={(value: string) => setEmail(value)}
              type={'email'}
              error={emailError}
              disabled={loadingCancelBill}
              required
            />
          </Col>
        )}
      </Row>
    )
  }

  const renderRetryNullify = (): ReactElement => {
    return (
      <Card
        style={{
          backgroundColor: '#fffbeb',
        }}>
        <Paragraph
          size={'big'}
          style={{
            color: '#a06e4e',
          }}>
          Esta venta ya fue anulada. Solo puedes emitir una{' '}
          <strong>nota de crédito total.</strong> o{' '}
          <strong>Re-enviar a anular la factura</strong>
        </Paragraph>
        <br />
        <Paragraph
          size={'medium'}
          style={{
            color: '#a06e4e',
          }}>
          El estado <strong>Anulación con errores</strong> indica que la anulación de la
          DTE tuvo problemas, comunicate con tu asesor de soporte para recibir mayor
          información
        </Paragraph>
      </Card>
    )
  }

  return (
    <Modal
      show={show}
      centered
      size={retry ? undefined : 'lg'}
      onHide={() => (loadingCancelBill ? undefined : onClose(false))}>
      <Modal.Header closeButton={!loadingCancelBill}>
        <Modal.Title>Anular orden: {number}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{!retry ? renderNullify() : renderRetryNullify()}</Modal.Body>
      <Modal.Footer>
        <Row className={'container-buttons'}>
          <Button
            disabled={loadingCancelBill || loadingVerifyOrder}
            color={'secondary'}
            icon={faWindowClose}
            onClick={() => onClose(false)}
            dataCy={'button-close'}>
            Cerrar
          </Button>
          <Button
            dataCy={'button-nullify'}
            disabled={loadingVerifyOrder}
            loading={loadingCancelBill}
            icon={faFileInvoice}
            onClick={onNullify}>
            {retry ? 'Re-enviar a anular DTE' : 'Anular'}
          </Button>
        </Row>
      </Modal.Footer>
    </Modal>
  )
}
export default OrderNullify
